import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styles from './CompaniesPage.module.css';
import AppSidebar from '../../components/AppSidebar/AppSidebar';
import AddCompanyButton from '../../components/AddCompanyButton/AddCompanyButton';
import companiesApi from '../../api/companiesApi';

const SURVEY_LABELS = {
  hra: 'HRA',
  healthburnout: 'HRA & Burnout',
  estresburnout: 'Estrés y Burnout',
  nom035: 'NOM-035',
  nom035seci: 'NOM-035 Guía I',
  generalhealtquestionarie: 'Evaluación Integral de Bienestar Emocional',
  seguimiento: 'Seguimiento',
};
const EMPTY_DIRECTORY = { items: [], total: 0, page: 1, totalPages: 1 };

function serviceLabel(application) {
  return application.type === 'questionnaire'
    ? SURVEY_LABELS[String(application.service || '').toLowerCase()] || application.service
    : application.service;
}

function applicationDateKey(application) {
  if (!application.applicationDate) return 'undated';
  const date = new Date(application.applicationDate);
  if (Number.isNaN(date.getTime())) return 'undated';
  const iso = date.toISOString();
  if (application.applicationDatePrecision === 'year') return `year:${iso.slice(0, 4)}`;
  if (application.applicationDatePrecision === 'month') return `month:${iso.slice(0, 7)}`;
  if (application.applicationDatePrecision === 'day') return `day:${iso.slice(0, 10)}`;
  return `datetime:${iso}`;
}

function formatApplicationDate(application) {
  if (!application.applicationDate) return 'Sin fecha de aplicación';
  const date = new Date(application.applicationDate);
  if (Number.isNaN(date.getTime())) return 'Sin fecha de aplicación';
  if (application.applicationDatePrecision === 'year') {
    return String(date.getUTCFullYear());
  }
  if (application.applicationDatePrecision === 'month') {
    return date.toLocaleDateString('es-MX', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  }
  if (application.applicationDatePrecision === 'day') {
    return date.toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric', timeZone: 'UTC' });
  }
  return date.toLocaleString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function CompanyRow({ company }) {
  const [selectedApplicationDate, setSelectedApplicationDate] = useState('all');
  const appliedServices = company.appliedServices || [];
  const activeElements = [...new Set(appliedServices.map(serviceLabel).filter(Boolean))];
  const applicationDates = [...new Map(appliedServices.map(application => [
    applicationDateKey(application),
    {
      label: formatApplicationDate(application),
      timestamp: application.applicationDate
        ? new Date(application.applicationDate).getTime()
        : Number.NEGATIVE_INFINITY,
    },
  ])).entries()].sort((left, right) => right[1].timestamp - left[1].timestamp);
  const visibleApplications = selectedApplicationDate === 'all'
    ? appliedServices
    : appliedServices.filter(application => applicationDateKey(application) === selectedApplicationDate);
  const applicationsByBranch = [...visibleApplications.reduce((groups, application) => {
    const branchKey = application.branchId || 'unassigned';
    if (!groups.has(branchKey)) groups.set(branchKey, {
      branchId: branchKey,
      branchName: application.branchName || 'Sin sucursal',
      applications: [],
    });
    groups.get(branchKey).applications.push(application);
    return groups;
  }, new Map()).values()];
  const createdAt = company.created_at
    ? new Date(company.created_at).toLocaleDateString('es-MX', { timeZone: 'UTC' })
    : null;

  return (
    <li className={styles.row}>
      <details className={styles.companyDetails}>
        <summary className={styles.summary}>
          <span className={styles.identity}>
            <span className={styles.avatar} aria-hidden="true">
              {company.logoUrl
                ? <img src={company.logoUrl} alt="" />
                : company.name.trim().slice(0, 2).toUpperCase()}
            </span>
            <span className={styles.companyHeading}>
              <span className={styles.companyName}>{company.name}</span>
              <span className={styles.muted}>
                {createdAt ? `Registro: ${createdAt}` : 'Sin fecha de registro'}
              </span>
            </span>
          </span>
          <span className={styles.rowStat}><strong>{company.employeesNumber}</strong> empleados</span>
          <span className={styles.rowStat}><strong>{appliedServices.length}</strong> aplicaciones</span>
          <svg className={styles.chevron} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </summary>
        <div className={styles.detailsContent}>
          <section className={styles.activeSection}>
            <h2>Servicios y cuestionarios aplicados ({activeElements.length})</h2>
            {activeElements.length > 0 ? (
              <ul className={styles.badges}>
                {activeElements.map(element => <li className={styles.badge} key={element}>{element}</li>)}
              </ul>
            ) : <p className={styles.muted}>Esta empresa no tiene servicios ni cuestionarios aplicados.</p>}
          </section>
          {appliedServices.length > 0 && (
            <section className={styles.applicationsSection}>
              <div className={styles.applicationsHeader}>
                <h2>Aplicaciones por sucursal</h2>
                {applicationDates.length > 1 && (
                  <label className={styles.applicationDateFilter}>
                    Fecha de aplicación
                    <select
                      value={selectedApplicationDate}
                      onChange={event => setSelectedApplicationDate(event.target.value)}
                    >
                      <option value="all">Todas las fechas</option>
                      {applicationDates.map(([key, { label }]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
              <div className={styles.branchApplications}>
                {applicationsByBranch.map(branch => (
                  <article key={branch.branchId} className={styles.branchApplicationCard}>
                    <h3>{branch.branchName}</h3>
                    <ul>
                      {branch.applications.map((application, index) => (
                        <li key={`${application.type}-${application.applicationName}-${applicationDateKey(application)}-${index}`}>
                          <div>
                            <strong>{serviceLabel(application)}</strong>
                            <span>{application.applicationName}</span>
                          </div>
                          <div className={styles.applicationMeta}>
                            <span>{formatApplicationDate(application)}</span>
                            {application.type !== 'questionnaire' && (
                              <span>{application.recordCount} registros</span>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </article>
                ))}
              </div>
            </section>
          )}
          <div className={styles.actions}>
            <Link className={styles.secondaryButton} to={`/company/${company._id}`}>Editar empresa</Link>
            <Link className={styles.primaryButton} to={`/${company._id}/dashboard`}>Ver dashboard</Link>
          </div>
        </div>
      </details>
    </li>
  );
}

export default function CompaniesPage() {
  const [directory, setDirectory] = useState(EMPTY_DIRECTORY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    const timer = setTimeout(async () => {
      try {
        const data = await companiesApi.getCompanyDirectory({ search, page }, controller.signal);
        if (!controller.signal.aborted) setDirectory(data);
      } catch (err) {
        if (!controller.signal.aborted) setError('No se pudieron cargar las empresas. Intenta de nuevo.');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 250);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [search, page, retry]);

  const changeFilter = (setter, value) => {
    setLoading(true);
    setter(value);
    setPage(1);
  };
  const changePage = (nextPage) => {
    setLoading(true);
    setPage(nextPage);
  };
  const firstItem = directory.total ? (directory.page - 1) * 10 + 1 : 0;
  const lastItem = Math.min(directory.page * 10, directory.total);

  return (
    <div className={styles.container}>
      <AppSidebar />
      <main className={styles.main}>
        <header className={styles.pageHeader}>
          <div>
            <h1>Empresas</h1>
            <p className={styles.muted}>Consulta sus cuestionarios, empleados y estudios.</p>
          </div>
          <AddCompanyButton to="/company" />
        </header>
        <div className={styles.controls}>
          <label className={styles.searchField} htmlFor="company-search">
            Buscar por nombre
            <input
              id="company-search"
              type="search"
              placeholder="Buscar empresa..."
              value={search}
              onChange={event => changeFilter(setSearch, event.target.value)}
              className={styles.search}
            />
          </label>
        </div>
        <div aria-busy={loading}>
          {loading ? (
            <div role="status" className={styles.loading}>
              <span>Cargando empresas…</span>
              {Array.from({ length: 5 }, (_, index) => <div key={index} className={styles.skeletonRow} />)}
            </div>
          ) : error ? (
            <div className={styles.empty} role="alert">
              <p>{error}</p>
              <button className={styles.secondaryButton} onClick={() => setRetry(value => value + 1)}>Reintentar</button>
            </div>
          ) : directory.items.length > 0 ? (
            <>
              <p className={styles.resultCount} role="status">Mostrando {firstItem}–{lastItem} de {directory.total} empresas</p>
              <ul className={styles.companyList} aria-label="Empresas">
                {directory.items.map(company => <CompanyRow key={company._id} company={company} />)}
              </ul>
              <nav className={styles.pagination} aria-label="Paginación de empresas">
                <span>10 empresas por página</span>
                <div>
                  <button className={styles.secondaryButton} disabled={directory.page === 1} onClick={() => changePage(directory.page - 1)}>Anterior</button>
                  <span>Página {directory.page} de {directory.totalPages}</span>
                  <button className={styles.secondaryButton} disabled={directory.page === directory.totalPages} onClick={() => changePage(directory.page + 1)}>Siguiente</button>
                </div>
              </nav>
            </>
          ) : <p className={styles.empty} role="status">No hay empresas que coincidan con los filtros.</p>}
        </div>
      </main>
    </div>
  );
}
